/**
 * Find & Replace across the publication (text boxes and table cells).
 * Matches are located in the plain text that `htmlToText` derives from a text box's HTML, so a match can be
 * selected inside the contentEditable (see selectPlainRange) and replaced with execCommand('insertText'),
 * which keeps the surrounding formatting.
 */
import { htmlToText, sanitizeHtml, escapeHtml } from './textUtils';
import { isTextType } from './selection';

const escapeRegExp = (s) => String(s).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

export const buildMatcher = (query, { matchCase = false, wholeWord = false } = {}) => {
  if (!query) return null;
  const core = escapeRegExp(query);
  const src = wholeWord ? `(?<![\\p{L}\\p{N}_])${core}(?![\\p{L}\\p{N}_])` : core;
  try {
    return new RegExp(src, `gu${matchCase ? '' : 'i'}`);
  } catch {
    return new RegExp(core, `g${matchCase ? '' : 'i'}`);
  }
};

/** Plain text of a text object exactly as the editor derives it (html wins over text). */
export const objectPlainText = (o) => {
  const p = o?.properties || {};
  return p.html ? htmlToText(sanitizeHtml(p.html)) : String(p.text ?? '');
};

/**
 * All matches in page order. Each: { pageIndex, objectId, start, end, text, cell?: [r, c] }
 * Linked continuation boxes are skipped (their text belongs to the head box).
 */
export const findAllMatches = (pages, query, opts) => {
  const re = buildMatcher(query, opts);
  const out = [];
  if (!re) return out;
  (pages || []).forEach((pg, pageIndex) => {
    (pg.objects || []).forEach((o) => {
      if (o.visible === false) return;
      if (isTextType(o)) {
        if (o.properties?.linkPrevId) return;
        const text = objectPlainText(o);
        for (const m of text.matchAll(re)) out.push({ pageIndex, objectId: o.id, start: m.index, end: m.index + m[0].length, text: m[0] });
      } else if (o.type === 'table') {
        (o.properties?.cells || []).forEach((row, r) => (row || []).forEach((cell, c) => {
          for (const m of String(cell?.text || '').matchAll(re)) out.push({ pageIndex, objectId: o.id, cell: [r, c], start: m.index, end: m.index + m[0].length, text: m[0] });
        }));
      }
    });
  });
  return out;
};

/**
 * Build a DOM Range for [start, end) of the plain text inside a contentEditable, mirroring htmlToText:
 * text nodes contribute their characters, <br> and the end of a block (div/p/li) contribute "\n".
 */
export const plainRange = (el, start, end) => {
  if (!el) return null;
  const range = document.createRange();
  let offset = 0;
  let startSet = false;
  let done = false;
  const walk = (node) => {
    if (done) return;
    if (node.nodeType === Node.TEXT_NODE) {
      const len = node.nodeValue.length;
      if (!startSet && start <= offset + len) { range.setStart(node, Math.max(0, start - offset)); startSet = true; }
      if (startSet && end <= offset + len) { range.setEnd(node, Math.max(0, end - offset)); done = true; return; }
      offset += len;
      return;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return;
    if (node.hasAttribute('data-wp-exclusion')) return;
    if (node.nodeName === 'BR') { offset += 1; return; }
    for (const child of Array.from(node.childNodes)) { walk(child); if (done) return; }
    if (node !== el && /^(DIV|P|LI)$/.test(node.nodeName)) offset += 1;
  };
  walk(el);
  if (!startSet) return null;
  if (!done) range.setEnd(el, el.childNodes.length);
  return range;
};

/** Select [start, end) of the plain text inside a contentEditable (moves the DOM selection / focus there). */
export const selectPlainRange = (el, start, end) => {
  const range = plainRange(el, start, end);
  if (!range) return false;
  const sel = window.getSelection();
  sel.removeAllRanges();
  sel.addRange(range);
  return true;
};

const FIND_HIGHLIGHT = 'wp-find-match';
export const supportsHighlightApi = () => typeof window !== 'undefined' && !!window.CSS?.highlights && typeof window.Highlight === 'function';

/**
 * Paint the current match with the CSS Custom Highlight API (::highlight(wp-find-match)) so the found text stays
 * visible while keyboard focus remains in the Find pane. Returns the Range (or null when it could not be located).
 */
export const highlightPlainRange = (el, start, end) => {
  const range = plainRange(el, start, end);
  if (!range) return null;
  if (supportsHighlightApi()) window.CSS.highlights.set(FIND_HIGHLIGHT, new window.Highlight(range));
  return range;
};

export const clearFindHighlight = () => { if (supportsHighlightApi()) window.CSS.highlights.delete(FIND_HIGHLIGHT); };

/** Replace every match inside the text nodes of an HTML fragment (formatting preserved). */
export const replaceInHtml = (html, re, replacement) => {
  const div = document.createElement('div');
  div.innerHTML = html;
  const walker = document.createTreeWalker(div, NodeFilter.SHOW_TEXT);
  const nodes = [];
  let n;
  while ((n = walker.nextNode())) nodes.push(n);
  let count = 0;
  nodes.forEach((t) => {
    re.lastIndex = 0;
    if (!re.test(t.nodeValue)) return;
    re.lastIndex = 0;
    t.nodeValue = t.nodeValue.replace(re, () => { count += 1; return replacement; });
  });
  return { html: div.innerHTML, count };
};

/**
 * Replace all matches in every text box / table cell. Returns [{ id, props }] updates and the total count.
 */
export const replaceAllInPages = (pages, query, replacement, opts) => {
  const re = buildMatcher(query, opts);
  const updates = [];
  let total = 0;
  if (!re) return { updates, total };
  (pages || []).forEach((pg, pageIndex) => {
    (pg.objects || []).forEach((o) => {
      if (o.visible === false) return;
      const p = o.properties || {};
      if (isTextType(o)) {
        if (p.linkPrevId) return;
        if (p.html) {
          const { html, count } = replaceInHtml(p.html, re, replacement);
          if (count) { total += count; updates.push({ pageIndex, id: o.id, props: { html, text: htmlToText(html) } }); }
        } else {
          let count = 0;
          re.lastIndex = 0;
          const text = String(p.text ?? '').replace(re, () => { count += 1; return replacement; });
          if (count) { total += count; updates.push({ pageIndex, id: o.id, props: { text, html: undefined } }); }
        }
      } else if (o.type === 'table' && Array.isArray(p.cells)) {
        let count = 0;
        const cells = p.cells.map((row) => (row || []).map((cell) => {
          re.lastIndex = 0;
          const text = String(cell?.text || '').replace(re, () => { count += 1; return replacement; });
          return { ...cell, text };
        }));
        if (count) { total += count; updates.push({ pageIndex, id: o.id, props: { cells } }); }
      }
    });
  });
  return { updates, total };
};

export const highlightHtml = (text, query) => escapeHtml(text).replace(buildMatcher(query, {}) || /$^/, (m) => `<mark>${m}</mark>`);
